import { useEffect, useRef, useState } from 'react'
import { Button, Card, CardContent, cn } from '@saas-pages/shared/ui'
import { toolError, toolInvoke, toolSuccess } from '@saas-pages/shared/public/runtime'
import { format, useFormatBytes, useT, type Translations } from '@/shared/i18n'
import { getUtilityDefinition } from '@/shared/utilities/utilities'
import {
  MAX_FILE_MB,
  extractImagesFromPdf,
  imageZipFilename,
  packImagesZip,
  validateFileSize,
  type ExtractImagesProgress,
  type ExtractedPdfImage,
} from './lib'
import { FilePickPrompt } from '@/shared/ui/FilePickPrompt'

const UTILITY = getUtilityDefinition('extract-images-from-pdf').analyticsKey

type ImageResult = ExtractedPdfImage & {
  objectUrl: string
}

type Phase =
  | { kind: 'idle' }
  | { kind: 'ready'; file: File }
  | { kind: 'loading'; file: File; progress: ExtractImagesProgress }
  | {
      kind: 'success'
      file: File
      images: ImageResult[]
      pageCount: number
      totalBytes: number
      zipUrl: string | null
      zipFilename: string | null
      truncated: boolean
    }
  | { kind: 'error'; file: File | null; message: string; errorCode: string }

interface DropZoneProps {
  onFile: (file: File) => void
  disabled: boolean
}

function DropZone({ onFile, disabled }: DropZoneProps) {
  const t = useT()
  const inputRef = useRef<HTMLInputElement>(null)
  const [dragging, setDragging] = useState(false)

  function handleFiles(files: FileList | null) {
    if (!files || files.length === 0) return
    const file = files[0]
    if (!file || !file.name.toLowerCase().endsWith('.pdf')) return
    onFile(file)
  }

  return (
    <div
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-label={t.common.pickPdf}
      onClick={() => !disabled && inputRef.current?.click()}
      onKeyDown={(event) => {
        if (!disabled && (event.key === 'Enter' || event.key === ' ')) {
          inputRef.current?.click()
        }
      }}
      onDragOver={(event) => {
        event.preventDefault()
        if (!disabled) setDragging(true)
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(event) => {
        event.preventDefault()
        setDragging(false)
        if (!disabled) handleFiles(event.dataTransfer.files)
      }}
      className={cn(
        'flex cursor-pointer select-none flex-col items-center justify-center gap-3',
        'rounded-xl border-2 border-dashed p-6 sm:p-10 text-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 transition-colors',
        dragging && !disabled
          ? 'border-primary bg-primary/5'
          : 'border-border hover:border-primary/50 hover:bg-accent/30',
        disabled && 'pointer-events-none opacity-50',
      )}
    >
      <input
        ref={inputRef}
        type="file"
        accept=".pdf,application/pdf"
        className="sr-only"
        tabIndex={-1}
        disabled={disabled}
        onChange={(event) => {
          handleFiles(event.target.files)
          event.currentTarget.value = ''
        }}
      />
      <div className="text-4xl" aria-hidden>
        IMG
      </div>
      <div>
        <p className="text-sm font-medium text-foreground">
          <FilePickPrompt drag={t.common.dragPdf} />
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          {format(t.common.pdfLimit, { size: MAX_FILE_MB })}
        </p>
        <p className="mt-1 text-[11px] text-muted-foreground/80">
          {t.common.localBadge}
        </p>
      </div>
    </div>
  )
}

function FileBadge({
  file,
  onClear,
  disabled,
}: {
  file: File
  onClear: () => void
  disabled: boolean
}) {
  const t = useT()
  const formatBytes = useFormatBytes()

  return (
    <div className="flex items-center gap-3 rounded-lg border bg-accent/30 px-4 py-3">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary/10 text-sm font-semibold text-primary">
        PDF
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-foreground">{file.name}</p>
        <p className="text-xs text-muted-foreground">{formatBytes(file.size)}</p>
      </div>
      <button
        type="button"
        onClick={onClear}
        disabled={disabled}
        aria-label={t.common.deleteFile}
        className={cn(
          'shrink-0 rounded p-1 text-muted-foreground transition-colors',
          'hover:bg-destructive/10 hover:text-destructive',
          disabled && 'pointer-events-none opacity-50',
        )}
      >
        ×
      </button>
    </div>
  )
}

function ScopeNotice() {
  const t = useT()

  return (
    <div className="rounded-lg border border-amber-300/60 bg-amber-50 px-4 py-3 text-sm text-amber-950 dark:border-amber-500/40 dark:bg-amber-950/30 dark:text-amber-100">
      <p className="font-medium">{t.extractPdfImages.scopeTitle}</p>
      <p className="mt-1">{t.extractPdfImages.scopeBody}</p>
    </div>
  )
}

function ProgressPanel({ progress }: { progress: ExtractImagesProgress }) {
  const t = useT()
  const total = progress.total || 1
  const percent = Math.round((progress.current / total) * 100)

  return (
    <Card>
      <CardContent className="space-y-3 pt-5">
        <div className="flex items-center justify-between gap-3 text-sm">
          <span className="font-medium text-foreground">{t.extractPdfImages.progressTitle}</span>
          <span className="text-muted-foreground">
            {format(t.extractPdfImages.progressPages, {
              current: progress.current,
              total: progress.total,
            })}
          </span>
        </div>
        <div className="h-2 overflow-hidden rounded-full bg-muted">
          <div className="h-full rounded-full bg-primary" style={{ width: `${percent}%` }} />
        </div>
      </CardContent>
    </Card>
  )
}

function ErrorPanel({ message, onRetry }: { message: string; onRetry: () => void }) {
  const t = useT()

  return (
    <Card className="border-destructive/30 bg-destructive/5">
      <CardContent className="pt-5">
        <p className="mb-2 text-sm font-semibold text-destructive">{t.common.errorTitle}</p>
        <p className="mb-4 text-sm text-foreground">{message}</p>
        <Button variant="outline" size="sm" onClick={onRetry}>
          {t.common.retry}
        </Button>
      </CardContent>
    </Card>
  )
}

function NoImagesPanel({ pageCount, onReset }: { pageCount: number; onReset: () => void }) {
  const t = useT()

  return (
    <Card>
      <CardContent className="space-y-4 pt-6">
        <div>
          <p className="text-sm font-semibold text-foreground">{t.extractPdfImages.noImagesTitle}</p>
          <p className="mt-1 text-sm text-muted-foreground">
            {format(t.extractPdfImages.noImagesBody, { pages: pageCount })}
          </p>
        </div>
        <ScopeNotice />
        <Button type="button" variant="outline" onClick={onReset}>
          {t.extractPdfImages.clear}
        </Button>
      </CardContent>
    </Card>
  )
}

function ResultPanel({
  phase,
  onReset,
}: {
  phase: Extract<Phase, { kind: 'success' }>
  onReset: () => void
}) {
  const t = useT()
  const formatBytes = useFormatBytes()

  if (phase.images.length === 0) {
    return <NoImagesPanel pageCount={phase.pageCount} onReset={onReset} />
  }

  return (
    <Card>
      <CardContent className="space-y-5 pt-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-foreground">{t.common.done}</p>
            <p className="mt-1 text-xs text-muted-foreground">
              {format(t.extractPdfImages.resultStats, {
                images: phase.images.length,
                pages: phase.pageCount,
                size: formatBytes(phase.totalBytes),
              })}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {phase.zipUrl && phase.zipFilename && (
              <Button asChild>
                <a href={phase.zipUrl} download={phase.zipFilename}>
                  {t.extractPdfImages.downloadZip}
                </a>
              </Button>
            )}
            <Button type="button" variant="outline" onClick={onReset}>
              {t.extractPdfImages.clear}
            </Button>
          </div>
        </div>

        {phase.truncated && (
          <div className="rounded-lg border border-amber-300/60 bg-amber-50 px-4 py-3 text-sm text-amber-950 dark:border-amber-500/40 dark:bg-amber-950/30 dark:text-amber-100">
            {t.extractPdfImages.truncated}
          </div>
        )}

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {phase.images.map((image, index) => (
            <div key={`${image.page}-${image.filename}`} className="rounded-lg border bg-background p-3">
              <div className="mb-3 flex aspect-[4/3] items-center justify-center overflow-hidden rounded-md bg-muted">
                <img
                  src={image.objectUrl}
                  alt={format(t.extractPdfImages.previewAlt, { index: index + 1 })}
                  className="max-h-full max-w-full object-contain"
                />
              </div>
              <div className="mb-3 space-y-1 text-xs text-muted-foreground">
                <p className="truncate font-medium text-foreground">{image.filename}</p>
                <p>
                  {format(t.extractPdfImages.imageMeta, {
                    page: image.page,
                    width: image.width,
                    height: image.height,
                    size: formatBytes(image.blob.size),
                  })}
                </p>
              </div>
              <Button asChild variant="outline" size="sm" className="w-full">
                <a href={image.objectUrl} download={image.filename}>
                  {t.extractPdfImages.downloadOne}
                </a>
              </Button>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}

function Spinner() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="animate-spin"
      aria-hidden="true"
    >
      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
    </svg>
  )
}

function revokeSuccessUrls(phase: Phase) {
  if (phase.kind !== 'success') return
  phase.images.forEach((image) => URL.revokeObjectURL(image.objectUrl))
  if (phase.zipUrl) URL.revokeObjectURL(phase.zipUrl)
}

function classifyError(error: unknown, t: Translations): { code: string; message: string } {
  const text = error instanceof Error ? error.message : String(error)
  if (/password|encrypted/i.test(text)) {
    return { code: 'encrypted', message: t.extractPdfImages.encrypted }
  }
  if (/canvas-unavailable|encode-failed|out of memory/i.test(text)) {
    return { code: 'browser_memory', message: t.extractPdfImages.browserMemory }
  }
  return { code: 'extract_error', message: t.extractPdfImages.extractError }
}

export function IzvlechKartinkiIzPdfForm() {
  const t = useT()
  const [phase, setPhase] = useState<Phase>({ kind: 'idle' })
  const phaseRef = useRef<Phase>(phase)
  const abortRef = useRef<AbortController | null>(null)

  phaseRef.current = phase

  useEffect(() => {
    return () => {
      abortRef.current?.abort()
      revokeSuccessUrls(phaseRef.current)
    }
  }, [])

  const isLoading = phase.kind === 'loading'
  const currentFile =
    phase.kind === 'ready' || phase.kind === 'loading' || phase.kind === 'error'
      ? phase.file
      : null

  function handleFile(file: File) {
    if (!validateFileSize(file)) {
      toolError(UTILITY, 'client_too_large')
      setPhase({
        kind: 'error',
        file,
        errorCode: 'client_too_large',
        message: format(t.common.tooLarge, { size: MAX_FILE_MB }),
      })
      return
    }

    revokeSuccessUrls(phase)
    setPhase({ kind: 'ready', file })
  }

  function handleClear() {
    abortRef.current?.abort()
    revokeSuccessUrls(phase)
    setPhase({ kind: 'idle' })
  }

  function handleRetry() {
    if (phase.kind === 'error' && phase.file) {
      setPhase({ kind: 'ready', file: phase.file })
    } else {
      setPhase({ kind: 'idle' })
    }
  }

  async function handleSubmit() {
    if (phase.kind !== 'ready') return

    const file = phase.file
    const controller = new AbortController()
    abortRef.current = controller
    setPhase({ kind: 'loading', file, progress: { current: 0, total: 0 } })
    toolInvoke(UTILITY)
    const startMs = Date.now()

    try {
      const result = await extractImagesFromPdf({
        file,
        signal: controller.signal,
        onProgress: (progress) => {
          setPhase((previous) => (previous.kind === 'loading' ? { ...previous, progress } : previous))
        },
      })

      const images: ImageResult[] = result.images.map((image) => ({
        ...image,
        objectUrl: URL.createObjectURL(image.blob),
      }))
      const zipBlob = images.length > 1 ? await packImagesZip(images) : null
      const zipUrl = zipBlob ? URL.createObjectURL(zipBlob) : null
      const durationMs = Date.now() - startMs

      toolSuccess(UTILITY, durationMs, {
        images: images.length,
        pages: result.pageCount,
        output_bytes: result.totalBytes,
        delivery: images.length > 1 ? 'zip' : images.length === 1 ? 'png' : 'none',
      })

      setPhase({
        kind: 'success',
        file,
        images,
        pageCount: result.pageCount,
        totalBytes: result.totalBytes,
        zipUrl,
        zipFilename: zipUrl ? imageZipFilename(file.name) : null,
        truncated: result.truncated,
      })
    } catch (error: unknown) {
      if (error instanceof DOMException && error.name === 'AbortError') return
      const { code, message } = classifyError(error, t)
      toolError(UTILITY, code)
      setPhase({ kind: 'error', file, errorCode: code, message })
    }
  }

  if (phase.kind === 'success') {
    return (
      <div className="space-y-4">
        <ResultPanel phase={phase} onReset={handleClear} />
      </div>
    )
  }

  return (
    <div className="space-y-5">
      {currentFile ? (
        <FileBadge file={currentFile} onClear={handleClear} disabled={isLoading} />
      ) : (
        <DropZone onFile={handleFile} disabled={isLoading} />
      )}

      <ScopeNotice />

      {phase.kind === 'loading' && <ProgressPanel progress={phase.progress} />}
      {phase.kind === 'error' && <ErrorPanel message={phase.message} onRetry={handleRetry} />}

      <Button
        size="lg"
        className="w-full"
        disabled={phase.kind !== 'ready' || isLoading}
        onClick={handleSubmit}
      >
        {isLoading ? (
          <>
            <Spinner />
            {t.extractPdfImages.loading}
          </>
        ) : (
          t.extractPdfImages.submit
        )}
      </Button>
    </div>
  )
}
